import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { useTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CircleIcon from "@mui/icons-material/Circle";
import ChatBubble from "./ChatBubble";
import { useDesign } from "./DesignContext";
import {
  trackMemoCardPageView,
  trackQuestionsCompleted,
} from "./MemoCardAnalytics";

export const questions = [
  "What's your favorite memory together?",
  "What's a phrase they always say?",
  "What's your favorite activity to do together?",
  "What's something you're proud of them for?",
  "What's your funniest memory together?",
  "What's their biggest strength?",
  "What personality trait do you admire most?",
  "What trait did you inherit that you appreciate?",
  "When did you see them the happiest?",
  "What's one lesson they taught you?",
  "What do you wish you knew about them earlier?",
  "What do you hope to do together in the future?",
];

export const fakeResponses = [
  // What's your favorite memory together?
  [
    "Making pancakes on Saturday mornings—we'd always burn the first one and laugh about it.",
    "Taking the bus to the mall just to walk around and split a pretzel.",
  ],
  // What's a phrase they always say?
  [
    "'Text me when you get home'—literally every time I leave.",
    "She always says, 'Just bring a jacket, you'll thank me later.'",
    "'You can't pour from an empty cup'—she's big on taking care of yourself.",
    "'I'm not mad, I'm just disappointed.' Oof, classic.",
  ],
  // What's your favorite activity to do together?
  [
    "Walking around the neighborhood with coffee and gossiping about the neighbors.",
    "Watching old rom-coms and yelling at the characters.",
    "Plant shopping—she touches every single plant before picking one.",
    "Driving around aimlessly with music on, no destination.",
  ],
  // What's something you're proud of them for?
  [
    "Going back to college after having kids—it was wild to see her study at the kitchen table.",
    "Starting a small business out of our garage and making it work.",
    "Standing up for herself at work when it wasn't easy.",
    "Volunteering every week at the food bank, rain or shine.",
  ],
  // What's your funniest memory together?
  [
    "Trying to put up IKEA furniture and somehow turning the shelves into a triangle.",
    "That time she wore two different shoes to dinner and didn't notice until dessert.",
    "We got stuck in an escape room for way too long and she kept yelling at the fake clues.",
    "She once tried to do a TikTok dance with me and kicked over a lamp.",
  ],
  // What's their biggest strength?
  [
    "She's unshakable—like, no matter what happens, she figures it out.",
    "Being the calm one when everyone else is freaking out.",
    "She always knows what to say when I feel like everything's falling apart.",
    "She can make people feel safe just by being in the room.",
  ],
  // What personality trait do you admire most?
  [
    "She's actually funny without trying—like, not in a 'mom joke' way either.",
    "She just... gets people. Like she always knows when someone's having a bad day.",
    "She doesn't pretend to be someone she's not—ever.",
    "She always shows up, even when it's inconvenient.",
  ],
  // What trait did you inherit that you appreciate?
  [
    "She's super curious and asks questions about everything—I do that now too.",
    "Her 'don't let them see you sweat' vibe.",
    "She's annoyingly good at noticing small things—turns out I got that too.",
    "Her way of writing cards that make people cry (in a good way).",
  ],
  // When did you see them the happiest?
  [
    "When she saw me graduate. I've never seen her smile that wide.",
    "Every time we're all home for the holidays, you can just see it on her face.",
    "When she finally took that trip to Italy she'd been dreaming about.",
    "When we surprised her with a birthday brunch—she cried before we even sat down.",
  ],
  // What's one lesson they taught you?
  [
    "That you don't have to be loud to be strong.",
    "To never settle just because something feels safe.",
    "That being kind doesn't mean being a pushover.",
    "To always write thank-you cards, no matter how small the gift.",
  ],
  // What do you wish you knew about them earlier?
  [
    "That she used to sneak into concerts in high school.",
    "That she almost moved to New York to be a writer.",
    "How hard she worked to keep things together when I was too young to notice.",
    "How much she wanted to be an artist but never got the chance.",
  ],
  // What do you hope to do together in the future?
  [
    "Finally take that trip to the Pacific Northwest we always talk about.",
    "Spend a whole weekend baking her old recipes with zero rush.",
    "Go to a pottery class and make something completely lopsided.",
    "Take her to that restaurant she's always wanted to try—just the two of us.",
  ],
];

const Questions = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const [showTypingMessage, setShowTypingMessage] = useState(false);
  const { answers, setAnswers } = useDesign();

  useEffect(() => {
    trackMemoCardPageView("questions");
  }, []);

  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const getQuestionEmoji = (index) => {
    const emojis = ["💭", "💬", "✨", "😊", "🎉", "📚", "💝", "💌"];
    return emojis[index];
  };

  const handleAnswerChange = (value) => {
    if (!isTyping) {
      setIsTyping(true);
      setShowTypingMessage(true);
      setTimeout(() => {
        setShowTypingMessage(false);
      }, 1500);
    }
    setAnswers((prev) => ({
      ...prev,
      [currentQuestionIndex]: value,
    }));
    setTimeout(() => {
      setIsTyping(false);
    }, 1000);
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    } else {
      // Track completion when user finishes all questions
      const answeredCount = Object.keys(answers).length;
      const skippedCount = questions.length - answeredCount;
      trackQuestionsCompleted(answeredCount, skippedCount, answers);
      navigate("/memo-card/design");
    }
  };

  const handleSkip = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const answeredCount = Object.keys(answers).length;
  const canProceed = answeredCount >= 3;

  const questionThemes = [
    {
      color: "#f3e6fa",
      border: "#e2cdb2",
      emoji: "📸",
      bg: "linear-gradient(90deg, #f3e6fa 60%, #fff 100%)",
    },
    {
      color: "#ffe0ec",
      border: "#ffb6c1",
      emoji: "💬",
      bg: "linear-gradient(90deg, #ffe0ec 60%, #fff 100%)",
    },
    {
      color: "#fffbe6",
      border: "#ffe082",
      emoji: "🎨",
      bg: "linear-gradient(90deg, #fffbe6 60%, #fff 100%)",
    },
    {
      color: "#fff9c4",
      border: "#ffe082",
      emoji: "🏆",
      bg: "linear-gradient(90deg, #fff9c4 60%, #fff 100%)",
    },
    {
      color: "#e0f7fa",
      border: "#80deea",
      emoji: "😂",
      bg: "linear-gradient(90deg, #e0f7fa 60%, #fff 100%)",
    },
    {
      color: "#e8f5e9",
      border: "#a5d6a7",
      emoji: "💪",
      bg: "linear-gradient(90deg, #e8f5e9 60%, #fff 100%)",
    },
    {
      color: "#fce4ec",
      border: "#f8bbd0",
      emoji: "✨",
      bg: "linear-gradient(90deg, #fce4ec 60%, #fff 100%)",
    },
    {
      color: "#f3e6fa",
      border: "#e2cdb2",
      emoji: "🎭",
      bg: "linear-gradient(90deg, #f3e6fa 60%, #fff 100%)",
    },
    {
      color: "#e8f5e9",
      border: "#a5d6a7",
      emoji: "😊",
      bg: "linear-gradient(90deg, #e8f5e9 60%, #fff 100%)",
    },
    {
      color: "#e0f7fa",
      border: "#80deea",
      emoji: "📚",
      bg: "linear-gradient(90deg, #e0f7fa 60%, #fff 100%)",
    },
    {
      color: "#fffbe6",
      border: "#ffe082",
      emoji: "🔍",
      bg: "linear-gradient(90deg, #fffbe6 60%, #fff 100%)",
    },
    {
      color: "#fce4ec",
      border: "#f8bbd0",
      emoji: "🎯",
      bg: "linear-gradient(90deg, #fce4ec 60%, #fff 100%)",
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        background: questionThemes[currentQuestionIndex].bg,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        pb: { xs: "180px", sm: "160px" },
        transition: "background 0.5s ease-in-out",
      }}
    >
      {/* Progress Bar */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          bgcolor: "rgba(0,0,0,0.1)",
          zIndex: 1000,
        }}
      >
        <Box
          sx={{
            height: "100%",
            bgcolor: "primary.main",
            width: `${((currentQuestionIndex + 1) / questions.length) * 100}%`,
            transition: "width 0.3s ease-in-out",
          }}
        />
      </Box>

      {/* Progress Dots */}
      <Box
        sx={{
          position: "fixed",
          top: 16,
          left: 0,
          right: 0,
          zIndex: 1000,
          display: "flex",
          justifyContent: "center",
          gap: 1,
          px: 2,
        }}
      >
        {questions.map((_, index) => (
          <CircleIcon
            key={index}
            sx={{
              fontSize: 8,
              color:
                index <= currentQuestionIndex
                  ? "primary.main"
                  : "rgba(0,0,0,0.1)",
              transition: "color 0.3s ease",
            }}
          />
        ))}
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          p: 2,
          pt: 3,
          maxWidth: "600px",
          mx: "auto",
          width: "100%",
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestionIndex}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "1.5rem", sm: "1.8rem" },
                }}
              >
                {questions[currentQuestionIndex]}
              </Typography>
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 200, damping: 10 }}
                whileHover={{ scale: 1.2, rotate: 10 }}
              >
                <Typography
                  variant="h4"
                  sx={{ fontSize: { xs: "1.5rem", sm: "1.8rem" } }}
                >
                  {questionThemes[currentQuestionIndex].emoji}
                </Typography>
              </motion.div>
            </Box>

            {/* Animated Example Bubbles */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                mb: 2,
                minHeight: 64,
                alignItems: "stretch",
                justifyContent: "flex-start",
              }}
            >
              {fakeResponses[currentQuestionIndex]
                .slice(0, 2)
                .map((resp, i) => (
                  <motion.div
                    key={resp}
                    initial={{
                      opacity: 0,
                      y: 30,
                      scale: 0.95,
                      x: i % 2 === 0 ? -20 : 20,
                    }}
                    animate={{ opacity: 1, y: 0, scale: 1, x: 0 }}
                    transition={{
                      delay: 0.15 + i * 0.13,
                      type: "spring",
                      stiffness: 180,
                      damping: 14,
                    }}
                    whileHover={{ scale: 1.04, rotate: i % 2 === 0 ? -2 : 2 }}
                    whileTap={{ scale: 0.97, rotate: i % 2 === 0 ? -6 : 6 }}
                  >
                    <ChatBubble
                      text={resp}
                      align={i % 2 === 0 ? "left" : "right"}
                      color={questionThemes[currentQuestionIndex].color}
                      borderColor={questionThemes[currentQuestionIndex].border}
                      tailShape="angled"
                      themeMood={questionThemes[currentQuestionIndex]}
                    />
                  </motion.div>
                ))}
            </Box>

            {/* Input Area */}
            <Box
              sx={{
                position: "relative",
                mb: 4,
              }}
            >
              <motion.div
                animate={{
                  scale: isTyping ? 1.02 : 1,
                  boxShadow: isTyping
                    ? "0 0 20px rgba(124,92,255,0.15)"
                    : "0 0 0 rgba(124,92,255,0)",
                }}
                transition={{ duration: 0.3 }}
              >
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder={
                    currentQuestionIndex === 0
                      ? "Type your memory here…"
                      : "Add your own moment…"
                  }
                  variant="outlined"
                  value={answers[currentQuestionIndex] || ""}
                  onChange={(e) => handleAnswerChange(e.target.value)}
                  onFocus={() => setInputFocused(true)}
                  onBlur={() => setInputFocused(false)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "18px",
                      fontSize: "1.18rem",
                      background: "#faf7ff",
                      padding: "14px 18px",
                      boxShadow: inputFocused
                        ? "0 0 0 3px #e5d6ff, 0 2px 12px 0 rgba(124,92,255,0.08)"
                        : "0 1.5px 8px 0 rgba(124,92,255,0.04)",
                      border: "none",
                      transition: "box-shadow 0.25s, background 0.25s",
                      "& fieldset": {
                        border: "none",
                      },
                      "&:hover": {
                        background: "#f3eaff",
                      },
                    },
                    "& .MuiInputBase-input": {
                      padding: 0,
                      fontFamily: "inherit",
                      color: "#2d2346",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: "#b9b3c6",
                      opacity: 1,
                      fontSize: "1.13rem",
                      fontWeight: 400,
                      fontFamily: "inherit",
                    },
                  }}
                />
              </motion.div>
            </Box>
          </motion.div>
        </AnimatePresence>

        {/* Navigation Buttons */}
        {currentQuestionIndex < questions.length - 1 && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              p: 2,
              bgcolor: "rgba(255,255,255,0.95)",
              backdropFilter: "blur(8px)",
              zIndex: 1000,
              borderTop: "1px solid rgba(0,0,0,0.1)",
            }}
          >
            <motion.div
              style={{ flex: 1 }}
              whileHover={{
                scale: 1.05,
                boxShadow: "0px 0px 16px rgba(168,85,247,0.2)",
              }}
              whileTap={{ scale: 0.98 }}
            >
              <Button
                onClick={handleBack}
                disabled={currentQuestionIndex === 0}
                startIcon={<ArrowBackIcon />}
                sx={{
                  flex: 1,
                  py: 2,
                  borderRadius: "10px",
                  minHeight: "56px",
                  width: "100%",
                  fontSize: "1.13rem",
                  fontWeight: 700,
                  background:
                    currentQuestionIndex === 0
                      ? "linear-gradient(90deg, #e2e2e2, #d1d1d1)"
                      : "linear-gradient(90deg, #a855f7, #f472b6)",
                  color: "white",
                  boxShadow: "0 2px 12px 0 rgba(168,85,247,0.10)",
                  "&:hover": {
                    background:
                      currentQuestionIndex === 0
                        ? "linear-gradient(90deg, #e2e2e2, #d1d1d1)"
                        : "linear-gradient(90deg, #b366f8, #f583c0)",
                  },
                  "&:disabled": {
                    background: "linear-gradient(90deg, #e2e2e2, #d1d1d1)",
                    color: "#9e9e9e",
                  },
                }}
              >
                Back
              </Button>
            </motion.div>
            <motion.div
              style={{ flex: 1 }}
              whileHover={{
                scale: 1.05,
                boxShadow: "0px 0px 16px rgba(168,85,247,0.2)",
              }}
              whileTap={{ scale: 0.98 }}
            >
              <Button
                onClick={handleSkip}
                sx={{
                  flex: 1,
                  py: 2,
                  borderRadius: "10px",
                  minHeight: "56px",
                  width: "100%",
                  fontSize: "1.13rem",
                  fontWeight: 700,
                  background: "linear-gradient(90deg, #f3e6fa, #fce4ec)",
                  color: "#7c5cff",
                  boxShadow: "0 2px 12px 0 rgba(168,85,247,0.10)",
                  "&:hover": {
                    background: "linear-gradient(90deg, #ede3fa, #fce4ec)",
                  },
                }}
              >
                Skip
              </Button>
            </motion.div>
            <motion.div
              style={{ flex: 1 }}
              whileHover={{
                scale: 1.05,
                boxShadow: "0px 0px 16px rgba(168,85,247,0.2)",
              }}
              whileTap={{ scale: 0.98 }}
            >
              <Button
                onClick={handleNext}
                disabled={!answers[currentQuestionIndex]}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  flex: 1,
                  py: 2,
                  borderRadius: "10px",
                  minHeight: "56px",
                  width: "100%",
                  fontSize: "1.13rem",
                  fontWeight: 700,
                  background: !answers[currentQuestionIndex]
                    ? "linear-gradient(90deg, #e2e2e2, #d1d1d1)"
                    : "linear-gradient(90deg, #a855f7, #f472b6)",
                  color: "white",
                  boxShadow: "0 2px 12px 0 rgba(168,85,247,0.10)",
                  "&:hover": {
                    background: !answers[currentQuestionIndex]
                      ? "linear-gradient(90deg, #e2e2e2, #d1d1d1)"
                      : "linear-gradient(90deg, #b366f8, #f583c0)",
                  },
                  "&:disabled": {
                    background: "linear-gradient(90deg, #e2e2e2, #d1d1d1)",
                    color: "#9e9e9e",
                  },
                }}
              >
                Next
              </Button>
            </motion.div>
          </Box>
        )}
        {/* Show friendly go-back button if on last question and not enough answered */}
        {currentQuestionIndex === questions.length - 1 && answeredCount < 3 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              p: 2,
              bgcolor: "rgba(255,255,255,0.95)",
              backdropFilter: "blur(8px)",
              zIndex: 1000,
              borderTop: "1px solid rgba(0,0,0,0.1)",
              minHeight: "56px",
            }}
          >
            <Button
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
              sx={{
                color: "#7c5cff",
                background: "#f3e6fa",
                fontWeight: 600,
                borderRadius: 3,
                minHeight: "56px",
                fontSize: "1.08rem",
                boxShadow: "none",
                px: 3,
                "&:hover": {
                  background: "#ede3fa",
                },
              }}
            >
              Answer at least 3 questions
            </Button>
          </Box>
        )}
      </Box>

      {/* Floating CTA Bar - Show when answeredCount >= 3 */}
      <AnimatePresence>
        {answeredCount >= 3 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.4, type: "spring", stiffness: 120 }}
            style={{ position: "fixed", left: 0, right: 0, zIndex: 1000 }}
          >
            <Box
              sx={{
                position: "fixed",
                bottom:
                  currentQuestionIndex === questions.length - 1
                    ? 0
                    : { xs: 80, sm: 80 }, // above nav buttons if not last
                left: 0,
                right: 0,
                bgcolor: "rgba(255,255,255,0.95)",
                p: 2,
                borderTop: "1px solid rgba(0,0,0,0.1)",
                backdropFilter: "blur(8px)",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Button
                component={Link}
                to="/memo-card/design"
                variant="contained"
                fullWidth
                disabled={!canProceed}
                sx={{
                  py: 2,
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  borderRadius: 3,
                  minHeight: "56px",
                  maxWidth: 420,
                }}
              >
                {canProceed
                  ? "No more questions, please!"
                  : `Answer ${3 - answeredCount} more to continue`}
              </Button>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default Questions;
